import { format, isValid, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useAccountDrawer } from 'context/accountDrawer';
import {
  CronoExtensionStatus,
  useGetExtensionStatus,
} from 'hooks/services/crono-extension/useGetExtensionStatus';
import useGetUserCredits from 'hooks/services/user/useGetUserCredits';
import { RewriteError } from 'crono-fe-common/types/enums/rewriteError';
import { parseTaskTypeToString } from 'utils/businessLogic';
import PATH from 'crono-fe-common/constants/path';
import { TaskTodo } from 'crono-fe-common/types/cronoTaskTodo';
import RedirectSpanLink from '../RedirectSpanLink';
import { useSafeStrategyOverviewContext } from 'context/strategyDetail';
import { captureException } from 'utils/exceptionUtils';

interface RewriteWarningLabelProps {
  rewriteFailureReason: RewriteError;
  task: TaskTodo;
  onRedirect?: () => void;
  allowToOpenInsights?: boolean;
}

export const RewriteWarningLabel = ({
  rewriteFailureReason,
  task,
  onRedirect,
  allowToOpenInsights,
}: RewriteWarningLabelProps) => {
  const { openContactDrawer, openAccountDrawer, closeDrawer } =
    useAccountDrawer();
  const { data: extensionStatus } = useGetExtensionStatus();
  const navigate = useNavigate();
  const { data: creditsData } = useGetUserCredits();
  const credits = creditsData?.data?.data;

  const { prospectId, accountId, type: taskType, subtype: taskSubtype } = task;

  const defaultMessage = (
    <span>
      Rewrite with AI failed due to an unexpected system error. Try again or the{' '}
      <span>{parseTaskTypeToString(taskType, taskSubtype)}</span> will be sent
      with the default template.
    </span>
  );

  const handleRedirect = (redirectAction: () => void) => {
    redirectAction();
    if (onRedirect) {
      onRedirect();
    }
  };

  const {
    insightsContext: {
      setShowInsightTab,
      setRewriteEnabledMode,
      rewriteEnabledMode,
    },
  } = useSafeStrategyOverviewContext();

  const onRewriteWithAIClick = () => {
    setShowInsightTab((prevShowInsightTab) => {
      setRewriteEnabledMode((prev) =>
        prevShowInsightTab && prev ? null : 'sequenceInstance',
      );

      return !(prevShowInsightTab && rewriteEnabledMode);
    });
  };

  switch (rewriteFailureReason) {
    case RewriteError.PROSPECT_LINKEDIN_URL_MISSING:
      return (
        <span>
          Rewrite with AI failed due to missing prospect LinkedIn URL. Fix it{' '}
          {
            <RedirectSpanLink
              onClick={() => {
                if (accountId) {
                  handleRedirect(() => {
                    openContactDrawer({ prospectId, accountId });
                  });
                } else {
                  const debugData = {
                    taskId: task?.id,
                    prospectId: task?.prospectId,
                    type: task?.type,
                  };
                  console.error(
                    'Cannot open contact side tab because accountId is missing on task',
                    debugData,
                  );
                  captureException(
                    new Error(
                      'Cannot open contact side tab because accountId is missing on task: ' +
                        JSON.stringify(debugData),
                    ),
                  );
                }
              }}
            />
          }{' '}
          and rewrite again
          {allowToOpenInsights ? (
            <>
              {' '}
              <RedirectSpanLink onClick={onRewriteWithAIClick} />
            </>
          ) : (
            ''
          )}
          , or the <span>{parseTaskTypeToString(taskType, taskSubtype)}</span>{' '}
          will be sent with the default template.
        </span>
      );

    case RewriteError.COMPANY_LINKEDIN_URL_MISSING:
      return (
        <span>
          Rewrite with AI failed due to missing company LinkedIn URL. Fix it{' '}
          {
            <RedirectSpanLink
              onClick={() =>
                handleRedirect(() => {
                  if (accountId) {
                    openAccountDrawer({ accountId, forceReload: true });
                  }
                })
              }
            />
          }{' '}
          and rewrite again
          {allowToOpenInsights ? (
            <>
              {' '}
              <RedirectSpanLink onClick={onRewriteWithAIClick} />
            </>
          ) : (
            ''
          )}
          , or the <span>{parseTaskTypeToString(taskType, taskSubtype)}</span>{' '}
          will be sent with the default template.
        </span>
      );

    case RewriteError.EXTENSION_NOT_AVAILABLE_DURING_INSERT:
      return (
        <span>
          Rewrite with AI failed.{' '}
          {extensionStatus?.status ===
          CronoExtensionStatus.INSTALLED_WITH_LINKEDIN_COOKIE ? (
            <>
              Try again{' '}
              {allowToOpenInsights ? (
                <>
                  {' '}
                  <RedirectSpanLink onClick={onRewriteWithAIClick} />{' '}
                </>
              ) : (
                ''
              )}
              or the <span>{parseTaskTypeToString(taskType, taskSubtype)}</span>{' '}
              will be sent with the default template.
            </>
          ) : (
            <>
              Install the latest extension{' '}
              <RedirectSpanLink
                onClick={() =>
                  handleRedirect(() => {
                    navigate(PATH.SETTINGS.INTEGRATIONS.BROWSER_EXTENSION);
                    closeDrawer();
                  })
                }
              />{' '}
              or log in to LinkedIn. Then rewrite again
              {allowToOpenInsights ? (
                <>
                  {' '}
                  <RedirectSpanLink onClick={onRewriteWithAIClick} />
                </>
              ) : (
                ''
              )}
              .
            </>
          )}
        </span>
      );

    case RewriteError.CREDITS_RELATED_ERROR: {
      const getRenewalDate = () => {
        if (!credits?.creditsRenewalDate) return '';
        const date = parseISO(credits.creditsRenewalDate);
        return isValid(date) ? format(date, 'MMM dd, yyyy') : '';
      };
      return (
        <span>
          Rewrite with AI failed due to insufficient AI credits. Buy more{' '}
          <RedirectSpanLink
            onClick={() =>
              handleRedirect(() => {
                navigate(PATH.SETTINGS.ADD_ONS);
                closeDrawer();
              })
            }
          />{' '}
          or wait until next update on <span>{getRenewalDate()}</span>. Then
          rewrite again
          {allowToOpenInsights ? (
            <>
              {' '}
              <RedirectSpanLink onClick={onRewriteWithAIClick} />
            </>
          ) : (
            ''
          )}
          .
        </span>
      );
    }

    default:
      return defaultMessage;
  }
};
